/**
 * storage.js — Centralised localStorage abstraction layer.
 *
 * All localStorage keys are defined as constants in KEYS so that:
 *  - renaming a key is a single-line change
 *  - typos are caught at import time, not at runtime
 *  - incognito / strict-privacy browsers that throw SecurityError are handled
 *    gracefully in every read/write operation
 */

// ---------------------------------------------------------------------------
// Static key constants
// ---------------------------------------------------------------------------

/** Every localStorage key used by the application. */
export const KEYS = {
  // Auth / Session
  TOKEN: 'token',
  REFRESH_TOKEN: 'refresh_token',
  IS_AUTHENTICATED: 'isAuthenticated',
  LOGIN_DATA: 'logindata',
  USER: 'user',
  LOCAL_USER: 'localUser',
  COMPANY_ID: 'company_id',
  EMAIL: 'email',

  // Device / XMPP
  XMPP_TOKEN_ID: 'xmpp_tokenid',
  XMPP_RESOURCE: 'xmpp_resource',
  IS_TRUSTED: 'isTrusted',
  FIREBASE_TOKEN: 'firebase_token',

  // Remember-me credentials.
  // Active set: RMBR_EMAIL / RMBR_PASSWORD — written by Login.js when the user
  //   ticks "remember me" and read on the next login.
  // Legacy fallback: FREELI_EMAIL / FREELI_PASSWORD — read-only at login to
  //   pre-fill the form for users who logged in under the old system.
  // (REMEMBER_ME / REMEMBER_EMAIL / REMEMBER_PASSWORD were removed in 2026-05;
  //  they had no writers, only a no-op preservation block in removeUserSession.)
  RMBR_EMAIL: 'rmbrEmail',
  RMBR_PASSWORD: 'rmbrPassword',
  FREELI_EMAIL: 'freeli_email',
  FREELI_PASSWORD: 'freeli_password',

  // UI / Preferences
  THEME: 'theme',
  CONNECT_ACTIVE_TAB: 'connect_active_tab',
  ACTIVE_FREELI_CONV: 'active_freeli_conv',
  FILE_VIEW: 'file_view',
  SELECTED_USER_ID: 'selectedUserId',

  // State / Timestamps
  CURRENT_PAGINATION: 'current_pagination',
  APP_TS: 'app_ts',
  RELOAD_TIME: 'reload_time',
  TOTAL_LOGIN: 'total_login',

  // Error counters (OTP flow)
  EMAIL_ERROR_COUNTER: 'emailErrorCounter',
  VERIFY_ERROR_COUNTER: 'verifyErrorCounter',
  ERROR_COUNTER: 'errorCounter',

  // Misc
  GUEST_USER_ID: 'guest_user_id',
};

// ---------------------------------------------------------------------------
// Dynamic key generators
// Used where the key is constructed from a runtime value.
// ---------------------------------------------------------------------------

export const dynamicKey = {
  /**
   * Key for a persisted message draft.
   * @param {string} draftKey - Conversation or reply ID.
   * @returns {string}
   */
  draft: (draftKey) => `draft_${draftKey}`,

  /**
   * Key for a task's colour preference.
   * @param {string} taskId
   * @returns {string}
   */
  taskColor: (taskId) => `task-color-${taskId}`,

  /**
   * Key for a review-form's colour preference.
   * @param {string} reviewFormId
   * @returns {string}
   */
  reviewFormColor: (reviewFormId) => `reviewForm-color-${reviewFormId}`,

  /**
   * Key for the saved column order of a kanban view.
   * @param {string} viewType
   * @returns {string}
   */
  kanbanColumnOrder: (viewType) => `kanban_column_order_${viewType}`,
};

// ---------------------------------------------------------------------------
// Storage object
// ---------------------------------------------------------------------------

/**
 * Thin wrapper around localStorage that absorbs SecurityError exceptions
 * thrown by some browsers in incognito / strict-privacy mode.
 *
 * All methods silently no-op on failure — callers should treat a null return
 * from `get()` the same as a missing key.
 */
export const storage = {
  /**
   * Read a value from localStorage.
   *
   * @param {string} key - A key from KEYS or a dynamicKey result.
   * @returns {string|null} The raw stored string, or null if the key is absent
   *   or localStorage is unavailable.
   */
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },

  /**
   * Write a string value to localStorage.
   *
   * @param {string} key - A key from KEYS or a dynamicKey result.
   * @param {string} value - The value to persist. Pass JSON.stringify(x) for
   *   objects — this wrapper does not serialise automatically.
   * @returns {void}
   */
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // SecurityError (incognito) or QuotaExceededError — fail silently.
    }
  },

  /**
   * Remove a single key from localStorage.
   *
   * @param {string} key - A key from KEYS or a dynamicKey result.
   * @returns {void}
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // SecurityError in incognito — fail silently.
    }
  },

  /**
   * Clear ALL keys from localStorage.
   *
   * Use with caution — this wipes remember-me credentials and preferences in
   * addition to session data. Prefer explicit `remove()` calls where possible.
   *
   * @returns {void}
   */
  clear() {
    try {
      localStorage.clear();
    } catch (e) {
      // SecurityError in incognito — fail silently.
    }
  },
};
